import {
	AlertCircle,
	ArrowLeft,
	BadgeCheck,
	Check,
	ChevronRight,
	Clock,
	CreditCard,
	ExternalLink,
	Globe,
	Mail,
	MapPin,
	Pencil,
	Percent,
	Phone,
	Rss,
	Save,
	Send,
	ShieldCheck,
	Star,
	Store,
	Tag,
	TrendingUp,
	Truck,
	X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, SERVER_URL } from "../api/client";
import { CssInvitePanel } from "../components/merchants/CssInvitePanel";
import { ShopTextPanel } from "../components/merchants/ShopTextPanel";
import { TIERS, tierColor, tierLabel } from "../constants/tiers";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface FeedDetail {
	id: number;
	name: string;
	url: string;
	feedType: string;
	schedule: string;
	lastRun: string | null;
	active: boolean;
	assumedProductCount: number;
	createdAt: string;
	updatedAt: string;
}

interface MerchantDetail {
	id: number;
	name: string;
	website: string;
	tier: string;
	validated: boolean;
	address: string | null;
	affiliateId: string | null;
	buyGuarantee: boolean;
	city: string | null;
	contactEmail: string | null;
	contactPerson: string | null;
	country: string | null;
	description: string | null;
	logoUrl: string | null;
	phone: string | null;
	trustPilot: number | null;
	trustPilotUrl: string | null;
	zipcode: number | null;
	primaryCategory: string | null;
	semPpc: boolean;
	affiliatePcss?: boolean;
	shoppingAds: boolean;
	socialPpc: boolean;
	externalCategoryId: number | null;
	category: string | null;
	subCategory: string | null;
	returnPolicyDays: number | null;
	returnFees: "FreeReturn" | "ReturnShippingFees" | null;
	returnPolicyUrl: string | null;
	// Partner-ads feed fields (read-only — refreshed on each affiliate sync).
	commissionRate: string | null;
	clickRate: string | null;
	leadRate: string | null;
	epc: string | null;
	affiliateLink: string | null;
	programTerms: string | null;
	cashback: boolean | null;
	discountSites: boolean | null;
	semPpcRestriction: string | null;
	shoppingAdsRestriction: string | null;
	feedCurrency: string | null;
	feedMarket: string | null;
	hasFeed: boolean | null;
	feedUpdated: string | null;
	// Scraped trust/logistics signals (V65).
	paymentMethods: string[] | null;
	trustMarks: string[] | null;
	trustpilotRating: string | null;
	trustpilotReviewCount: number | null;
	freeShippingThreshold: string | null;
	shippingCostFrom: string | null;
	deliveryTimeMinDays: number | null;
	deliveryTimeMaxDays: number | null;
	warrantyInfo: string | null;
	refundInfo: string | null;
	scrapeSourceUrl: string | null;
	scrapedAt: string | null;
	createdAt: string;
	updatedAt: string;
	feeds: FeedDetail[];
}

// L6 acquisition evidence (Item 2) — mirrors PartnerAdsMerchantSummary in
// admin/apps/server/src/metrics/partner-ads-merchant-daily.ts field-for-field.
interface AcquisitionSummary {
	merchantId: number;
	programId: string | null;
	source: "Partner-ads";
	window: { from: string; to: string };
	days: number;
	daysWithClicks: number;
	clicksCoverage: "full" | "partial" | "none";
	clicks: number | null;
	conversions: number;
	revenueDkk: number;
	paidDkk: number;
	effectiveCpcDkk: number | null;
	commerceHubCpcDkk: number;
}

const ACQUISITION_WINDOWS = [
	{ days: 30, label: "30 dage" },
	{ days: 90, label: "90 dage" },
	{ days: 180, label: "180 dage" },
	{ days: 365, label: "1 år" },
] as const;

const kr = (n: number) =>
	`${n.toLocaleString("da-DK", { maximumFractionDigits: 2 })} kr`;

// ---------------------------------------------------------------------------
// Reusable components (mirroring Product page style)
// ---------------------------------------------------------------------------
const inputClass =
	"px-3 py-1.5 rounded-lg border border-primary/30 bg-white text-sm text-slate-800 outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all w-full";
const textareaClass = `${inputClass} resize-y min-h-[80px]`;
const labelClass =
	"text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1 block";

const Label = ({ children }: { children: React.ReactNode }) => (
	<span className={labelClass}>{children}</span>
);

const EditableField = ({
	label,
	value,
	editing,
	onChange,
	type = "text",
	multiline = false,
	placeholder,
}: {
	label: string;
	value: string;
	editing: boolean;
	onChange: (v: string) => void;
	type?: string;
	multiline?: boolean;
	placeholder?: string;
}) => (
	<div>
		<Label>{label}</Label>
		{editing ? (
			multiline ? (
				<textarea
					className={textareaClass}
					value={value}
					onChange={(e) => onChange(e.target.value)}
					placeholder={placeholder}
					rows={4}
				/>
			) : (
				<input
					type={type}
					className={inputClass}
					value={value}
					onChange={(e) => onChange(e.target.value)}
					placeholder={placeholder}
				/>
			)
		) : (
			<p
				className={`text-sm text-slate-700 ${!value ? "text-slate-400 italic" : ""}`}
			>
				{value || `No ${label.toLowerCase()}`}
			</p>
		)}
	</div>
);

const EditableToggle = ({
	label,
	value,
	editing,
	onChange,
}: {
	label: string;
	value: boolean;
	editing: boolean;
	onChange: (v: boolean) => void;
}) => (
	<div className="flex items-center gap-3">
		{editing ? (
			<button
				type="button"
				onClick={() => onChange(!value)}
				className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${value ? "bg-primary" : "bg-slate-200"}`}
			>
				<span
					className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${value ? "translate-x-5" : "translate-x-0"}`}
				/>
			</button>
		) : (
			<span
				className={`inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${value ? "bg-primary/10 text-primary" : "bg-slate-100 text-slate-400"}`}
			>
				{value ? "Yes" : "No"}
			</span>
		)}
		<span className="text-sm text-slate-600">{label}</span>
	</div>
);

const Section = ({
	title,
	icon: Icon,
	children,
	badge,
	actions,
}: {
	title: string;
	icon: React.ElementType;
	children: React.ReactNode;
	badge?: React.ReactNode;
	actions?: React.ReactNode;
}) => (
	<div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
		<div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
			<Icon className="w-4 h-4 text-slate-400" />
			<h2 className="text-sm font-semibold text-slate-700">{title}</h2>
			{badge}
			{actions && <div className="ml-auto">{actions}</div>}
		</div>
		<div className="p-6">{children}</div>
	</div>
);

// Mirrors SENDABLE_TEMPLATES in the admin server's mail/send-via-strapi.ts.
const LIFECYCLE_EMAILS = [
	{ slug: "trial-ending", label: "Prøveperiode udløber snart" },
	{ slug: "trial-ended", label: "Prøveperiode udløbet" },
	{ slug: "subscription-canceled", label: "Opsigelse bekræftet" },
] as const;

const FEED_TYPE_COLORS: Record<string, string> = {
	PARTNER_ADS: "bg-violet-100 text-violet-700",
	GOOGLE: "bg-blue-100 text-blue-700",
	ADTRACTION: "bg-orange-100 text-orange-700",
	TRADE_DOUBLER: "bg-rose-100 text-rose-700",
	PRICEONLINE: "bg-emerald-100 text-emerald-700",
};

// ---------------------------------------------------------------------------
// Merchant page
// ---------------------------------------------------------------------------
const Merchant = () => {
	const { id } = useParams<{ id: string }>();
	const [merchant, setMerchant] = useState<MerchantDetail | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [editingDetails, setEditingDetails] = useState(false);
	const [editingContact, setEditingContact] = useState(false);
	const [editingMarketing, setEditingMarketing] = useState(false);
	const [savingSection, setSavingSection] = useState<string | null>(null);

	const [logoDims, setLogoDims] = useState<{ w: number; h: number } | null>(
		null,
	);

	// ── Acquisition evidence (Partner-ads) ──
	const [acquisitionWindowDays, setAcquisitionWindowDays] = useState(90);
	const [acquisition, setAcquisition] = useState<AcquisitionSummary | null>(
		null,
	);
	const [acquisitionLoading, setAcquisitionLoading] = useState(true);
	const [acquisitionError, setAcquisitionError] = useState(false);

	// ── Lifecycle email sending ──
	const [sendingEmail, setSendingEmail] = useState<string | null>(null);
	const [emailResult, setEmailResult] = useState<{
		slug: string;
		ok: boolean;
		message: string;
	} | null>(null);

	// ── Editable state: details ──
	const [editName, setEditName] = useState("");
	const [editWebsite, setEditWebsite] = useState("");
	const [editTier, setEditTier] = useState("");
	const [editValidated, setEditValidated] = useState(false);
	const [editDescription, setEditDescription] = useState("");
	const [editLogoUrl, setEditLogoUrl] = useState("");
	const [editBuyGuarantee, setEditBuyGuarantee] = useState(false);
	const [editCategory, setEditCategory] = useState("");
	const [editSubCategory, setEditSubCategory] = useState("");
	const [editPrimaryCategory, setEditPrimaryCategory] = useState("");
	const [editReturnPolicyDays, setEditReturnPolicyDays] = useState("");
	const [editReturnFees, setEditReturnFees] = useState("");
	const [editReturnPolicyUrl, setEditReturnPolicyUrl] = useState("");

	// ── Editable state: contact ──
	const [editContactPerson, setEditContactPerson] = useState("");
	const [editContactEmail, setEditContactEmail] = useState("");
	const [editPhone, setEditPhone] = useState("");
	const [editAddress, setEditAddress] = useState("");
	const [editCity, setEditCity] = useState("");
	const [editCountry, setEditCountry] = useState("");
	const [editZipcode, setEditZipcode] = useState("");
	const [editTrustPilot, setEditTrustPilot] = useState("");
	const [editTrustPilotUrl, setEditTrustPilotUrl] = useState("");

	// ── Editable state: marketing ──
	const [editAffiliateId, setEditAffiliateId] = useState("");
	const [editSemPpc, setEditSemPpc] = useState(false);
	const [editShoppingAds, setEditShoppingAds] = useState(false);
	const [editSocialPpc, setEditSocialPpc] = useState(false);

	// ── Editable state: trust & policies (scraped, admin-correctable) ──
	const [editTrustpilotRating, setEditTrustpilotRating] = useState("");
	const [editTrustpilotReviewCount, setEditTrustpilotReviewCount] =
		useState("");
	const [editFreeShippingThreshold, setEditFreeShippingThreshold] =
		useState("");
	const [editShippingCostFrom, setEditShippingCostFrom] = useState("");
	const [editDeliveryTimeMinDays, setEditDeliveryTimeMinDays] = useState("");
	const [editDeliveryTimeMaxDays, setEditDeliveryTimeMaxDays] = useState("");
	const [editWarrantyInfo, setEditWarrantyInfo] = useState("");
	const [editRefundInfo, setEditRefundInfo] = useState("");
	const [editingTrust, setEditingTrust] = useState(false);

	const syncEditState = useCallback((m: MerchantDetail) => {
		setEditName(m.name);
		setEditWebsite(m.website);
		setEditTier(m.tier);
		setEditValidated(m.validated);
		setEditDescription(m.description ?? "");
		setEditLogoUrl(m.logoUrl ?? "");
		setEditBuyGuarantee(m.buyGuarantee);
		setEditCategory(m.category ?? "");
		setEditSubCategory(m.subCategory ?? "");
		setEditPrimaryCategory(m.primaryCategory ?? "");
		setEditReturnPolicyDays(m.returnPolicyDays?.toString() ?? "");
		setEditReturnFees(m.returnFees ?? "");
		setEditReturnPolicyUrl(m.returnPolicyUrl ?? "");
		setEditContactPerson(m.contactPerson ?? "");
		setEditContactEmail(m.contactEmail ?? "");
		setEditPhone(m.phone ?? "");
		setEditAddress(m.address ?? "");
		setEditCity(m.city ?? "");
		setEditCountry(m.country ?? "");
		setEditZipcode(m.zipcode?.toString() ?? "");
		setEditTrustPilot(m.trustPilot?.toString() ?? "");
		setEditTrustPilotUrl(m.trustPilotUrl ?? "");
		setEditAffiliateId(m.affiliateId ?? "");
		setEditSemPpc(m.semPpc);
		setEditShoppingAds(m.shoppingAds);
		setEditSocialPpc(m.socialPpc);
		setEditTrustpilotRating(m.trustpilotRating ?? "");
		setEditTrustpilotReviewCount(m.trustpilotReviewCount?.toString() ?? "");
		setEditFreeShippingThreshold(m.freeShippingThreshold ?? "");
		setEditShippingCostFrom(m.shippingCostFrom ?? "");
		setEditDeliveryTimeMinDays(m.deliveryTimeMinDays?.toString() ?? "");
		setEditDeliveryTimeMaxDays(m.deliveryTimeMaxDays?.toString() ?? "");
		setEditWarrantyInfo(m.warrantyInfo ?? "");
		setEditRefundInfo(m.refundInfo ?? "");
	}, []);

	// ── Fetch merchant ──
	const fetchMerchant = useCallback(async () => {
		if (!id) return;
		setLoading(true);
		setError(null);
		try {
			const { data, error: err } = await api.merchants({ id }).get();
			if (err || !data) throw new Error("Merchant not found");
			const m = data as unknown as MerchantDetail;
			setMerchant(m);
			syncEditState(m);
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to load merchant");
		} finally {
			setLoading(false);
		}
	}, [id, syncEditState]);

	useEffect(() => {
		fetchMerchant();
	}, [fetchMerchant]);

	// ── Acquisition evidence ──
	useEffect(() => {
		if (!id) return;
		let cancelled = false;

		const load = async () => {
			setAcquisitionLoading(true);
			setAcquisitionError(false);
			try {
				const { data, error: err } = await api
					.merchants({ id })
					.acquisition.get({ query: { days: String(acquisitionWindowDays) } });
				if (cancelled) return;
				if (err || !data) throw new Error("Failed to load acquisition data");
				setAcquisition(data as unknown as AcquisitionSummary);
			} catch {
				if (!cancelled) setAcquisitionError(true);
			} finally {
				if (!cancelled) setAcquisitionLoading(false);
			}
		};

		load();
		return () => {
			cancelled = true;
		};
	}, [id, acquisitionWindowDays]);

	// ── Lifecycle emails ──
	// Sends the same Strapi templates the webapp fires from Stripe webhooks, so
	// a merchant who missed one (or needs a nudge) can be reached from here.
	const handleSendLifecycleEmail = async (slug: string) => {
		if (!id) return;
		setSendingEmail(slug);
		setEmailResult(null);
		try {
			const res = await fetch(
				`${SERVER_URL}/merchants/${id}/send-lifecycle-email`,
				{
					method: "POST",
					credentials: "include",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ template: slug }),
				},
			);
			const data = (await res.json().catch(() => ({}))) as {
				email?: string;
				error?: string;
			};
			setEmailResult({
				slug,
				ok: res.ok,
				message: res.ok
					? `Sendt til ${data.email ?? "kontaktadressen"}`
					: (data.error ?? "Kunne ikke sende e-mail"),
			});
		} catch {
			setEmailResult({ slug, ok: false, message: "Kunne ikke sende e-mail" });
		} finally {
			setSendingEmail(null);
		}
	};

	// ── Save handlers ──
	const handleSaveDetails = async () => {
		if (!merchant || !id) return;
		setSavingSection("details");
		try {
			const { error: err } = await api.merchants({ id }).patch({
				name: editName,
				website: editWebsite,
				tier: editTier,
				validated: editValidated,
				description: editDescription || null,
				logoUrl: editLogoUrl || null,
				buyGuarantee: editBuyGuarantee,
				category: editCategory || null,
				subCategory: editSubCategory || null,
				primaryCategory: editPrimaryCategory || null,
				returnPolicyDays: editReturnPolicyDays
					? Number(editReturnPolicyDays)
					: null,
				returnFees:
					editReturnFees === "FreeReturn" ||
					editReturnFees === "ReturnShippingFees"
						? editReturnFees
						: null,
				returnPolicyUrl: editReturnPolicyUrl || null,
			});
			if (err) throw new Error("Save failed");
			setEditingDetails(false);
			await fetchMerchant();
		} catch {
			setError("Failed to save details");
		} finally {
			setSavingSection(null);
		}
	};

	const handleSaveContact = async () => {
		if (!merchant || !id) return;
		setSavingSection("contact");
		try {
			const { error: err } = await api.merchants({ id }).patch({
				contactPerson: editContactPerson || null,
				contactEmail: editContactEmail || null,
				phone: editPhone || null,
				address: editAddress || null,
				city: editCity || null,
				country: editCountry || null,
				zipcode: editZipcode ? Number(editZipcode) : null,
				trustPilot: editTrustPilot ? Number(editTrustPilot) : null,
				trustPilotUrl: editTrustPilotUrl || null,
			});
			if (err) throw new Error("Save failed");
			setEditingContact(false);
			await fetchMerchant();
		} catch {
			setError("Failed to save contact");
		} finally {
			setSavingSection(null);
		}
	};

	const handleSaveMarketing = async () => {
		if (!merchant || !id) return;
		setSavingSection("marketing");
		try {
			const { error: err } = await api.merchants({ id }).patch({
				affiliateId: editAffiliateId || null,
				semPpc: editSemPpc,
				shoppingAds: editShoppingAds,
				socialPpc: editSocialPpc,
			});
			if (err) throw new Error("Save failed");
			setEditingMarketing(false);
			await fetchMerchant();
		} catch {
			setError("Failed to save marketing");
		} finally {
			setSavingSection(null);
		}
	};

	const handleSaveTrust = async () => {
		if (!merchant || !id) return;
		setSavingSection("trust");
		const num = (v: string) => (v.trim() === "" ? null : Number(v));
		try {
			const { error: err } = await api.merchants({ id }).patch({
				trustpilotRating: num(editTrustpilotRating),
				trustpilotReviewCount: num(editTrustpilotReviewCount),
				freeShippingThreshold: num(editFreeShippingThreshold),
				shippingCostFrom: num(editShippingCostFrom),
				deliveryTimeMinDays: num(editDeliveryTimeMinDays),
				deliveryTimeMaxDays: num(editDeliveryTimeMaxDays),
				warrantyInfo: editWarrantyInfo || null,
				refundInfo: editRefundInfo || null,
			});
			if (err) throw new Error("Save failed");
			setEditingTrust(false);
			await fetchMerchant();
		} catch {
			setError("Failed to save trust & policies");
		} finally {
			setSavingSection(null);
		}
	};

	// ── Cancel handlers ──
	const handleCancelDetails = () => {
		if (merchant) syncEditState(merchant);
		setEditingDetails(false);
	};
	const handleCancelContact = () => {
		if (merchant) syncEditState(merchant);
		setEditingContact(false);
	};
	const handleCancelTrust = () => {
		if (merchant) syncEditState(merchant);
		setEditingTrust(false);
	};
	const handleCancelMarketing = () => {
		if (merchant) syncEditState(merchant);
		setEditingMarketing(false);
	};

	// ── Section edit action buttons ──
	const sectionEditActions = (
		isEditing: boolean,
		isSaving: boolean,
		onEdit: () => void,
		onSave: () => void,
		onCancel: () => void,
	) =>
		isEditing ? (
			<div className="flex items-center gap-2">
				<button
					type="button"
					onClick={onCancel}
					className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
				>
					<X className="w-3.5 h-3.5" /> Cancel
				</button>
				<button
					type="button"
					onClick={onSave}
					disabled={isSaving}
					className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold text-black bg-brand hover:bg-brand-hover transition-colors disabled:opacity-50"
				>
					{isSaving ? (
						<div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
					) : (
						<Save className="w-3.5 h-3.5" />
					)}
					Save
				</button>
			</div>
		) : (
			<button
				type="button"
				onClick={onEdit}
				className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
			>
				<Pencil className="w-3.5 h-3.5" /> Edit
			</button>
		);

	// ── Loading / error ──
	if (loading) {
		return (
			<div className="flex items-center justify-center py-32">
				<div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
			</div>
		);
	}

	if (error || !merchant) {
		return (
			<div className="flex flex-col items-center justify-center py-32 text-slate-400">
				<Store className="w-16 h-16 mb-4 text-slate-300" />
				<p className="text-lg font-semibold text-slate-500">
					{error ?? "Merchant not found"}
				</p>
				<Link
					to="/merchants"
					className="mt-4 text-sm text-primary hover:underline flex items-center gap-1"
				>
					<ArrowLeft className="w-4 h-4" /> Back to merchants
				</Link>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Breadcrumb */}
			<div className="flex items-center gap-2 text-sm text-slate-400">
				<Link to="/merchants" className="hover:text-primary transition-colors">
					Merchants
				</Link>
				<ChevronRight className="w-3.5 h-3.5" />
				<span className="text-slate-600 font-medium truncate max-w-md">
					{merchant.name}
				</span>
			</div>

			{/* ────────── Hero: Logo + Core Info ────────── */}
			<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
				{/* Logo / identity */}
				<div className="space-y-4">
					<div className="relative aspect-square bg-white rounded-2xl border border-slate-200 flex items-center justify-center overflow-hidden max-w-xs">
						{merchant.logoUrl ? (
							<>
								<img
									src={merchant.logoUrl}
									alt={merchant.name}
									className="w-full h-full object-contain p-6"
									onLoad={(e) => {
										const img = e.currentTarget;
										setLogoDims({ w: img.naturalWidth, h: img.naturalHeight });
									}}
								/>
								{logoDims && (
									<span className="absolute bottom-2 left-2 bg-slate-700/80 text-white text-[10px] leading-none px-1.5 py-1 rounded font-mono">
										{logoDims.w} × {logoDims.h}
									</span>
								)}
							</>
						) : (
							<Store className="w-20 h-20 text-slate-300" />
						)}
						{merchant.validated && (
							<span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-success/10 text-success">
								<ShieldCheck className="w-3.5 h-3.5" /> Verified
							</span>
						)}
					</div>
					<div className="flex items-center gap-2 flex-wrap">
						<span
							className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${tierColor(merchant.tier)}`}
						>
							{tierLabel(merchant.tier)}
						</span>
						{merchant.buyGuarantee && (
							<span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-success/10 text-success">
								<Check className="w-3 h-3" /> Buy Guarantee
							</span>
						)}
					</div>
				</div>

				{/* Core merchant info */}
				<div className="lg:col-span-2 space-y-6">
					<Section
						title="Merchant Details"
						icon={Store}
						actions={sectionEditActions(
							editingDetails,
							savingSection === "details",
							() => setEditingDetails(true),
							handleSaveDetails,
							handleCancelDetails,
						)}
					>
						<div className="space-y-4">
							<EditableField
								label="Name"
								value={editingDetails ? editName : merchant.name}
								editing={editingDetails}
								onChange={setEditName}
								placeholder="Merchant name"
							/>
							<div className="grid grid-cols-2 gap-4">
								<EditableField
									label="Website"
									value={editingDetails ? editWebsite : merchant.website}
									editing={editingDetails}
									onChange={setEditWebsite}
									placeholder="https://example.com"
								/>
								{editingDetails ? (
									<div>
										<Label>Tier</Label>
										<select
											className={inputClass}
											value={editTier}
											onChange={(e) => setEditTier(e.target.value)}
										>
											{(editTier &&
											!TIERS.includes(editTier as (typeof TIERS)[number])
												? [editTier, ...TIERS]
												: TIERS
											).map((tierValue) => (
												<option key={tierValue} value={tierValue}>
													{tierLabel(tierValue)}
												</option>
											))}
										</select>
									</div>
								) : (
									<div>
										<Label>Tier</Label>
										<span
											className={`inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${tierColor(merchant.tier)}`}
										>
											{tierLabel(merchant.tier)}
										</span>
									</div>
								)}
							</div>
							<div className="grid grid-cols-2 gap-4">
								<EditableToggle
									label="Verified"
									value={editingDetails ? editValidated : merchant.validated}
									editing={editingDetails}
									onChange={setEditValidated}
								/>
								<EditableToggle
									label="Buy Guarantee"
									value={
										editingDetails ? editBuyGuarantee : merchant.buyGuarantee
									}
									editing={editingDetails}
									onChange={setEditBuyGuarantee}
								/>
							</div>
							<EditableField
								label="Description"
								value={
									editingDetails
										? editDescription
										: (merchant.description ?? "")
								}
								editing={editingDetails}
								onChange={setEditDescription}
								multiline
								placeholder="Merchant description"
							/>
							<EditableField
								label="Logo URL"
								value={editingDetails ? editLogoUrl : (merchant.logoUrl ?? "")}
								editing={editingDetails}
								onChange={setEditLogoUrl}
								placeholder="https://example.com/logo.png"
							/>
							<div className="grid grid-cols-3 gap-4">
								<EditableField
									label="Primary Category"
									value={
										editingDetails
											? editPrimaryCategory
											: (merchant.primaryCategory ?? "")
									}
									editing={editingDetails}
									onChange={setEditPrimaryCategory}
									placeholder="Primary category"
								/>
								<EditableField
									label="Category"
									value={
										editingDetails ? editCategory : (merchant.category ?? "")
									}
									editing={editingDetails}
									onChange={setEditCategory}
									placeholder="Category"
								/>
								<EditableField
									label="Sub Category"
									value={
										editingDetails
											? editSubCategory
											: (merchant.subCategory ?? "")
									}
									editing={editingDetails}
									onChange={setEditSubCategory}
									placeholder="Sub category"
								/>
							</div>
							<div className="grid grid-cols-3 gap-4 pt-2 border-t border-slate-100">
								<EditableField
									label="Return days"
									type="number"
									value={
										editingDetails
											? editReturnPolicyDays
											: (merchant.returnPolicyDays?.toString() ?? "")
									}
									editing={editingDetails}
									onChange={setEditReturnPolicyDays}
									placeholder="14 (blank = statutory)"
								/>
								{editingDetails ? (
									<div>
										<Label>Return fees</Label>
										<select
											className={inputClass}
											value={editReturnFees}
											onChange={(e) => setEditReturnFees(e.target.value)}
										>
											<option value="">Not set (default)</option>
											<option value="FreeReturn">Free return</option>
											<option value="ReturnShippingFees">
												Customer pays return
											</option>
										</select>
									</div>
								) : (
									<div>
										<Label>Return fees</Label>
										<p className="text-sm text-slate-700">
											{merchant.returnFees === "FreeReturn"
												? "Free return"
												: merchant.returnFees === "ReturnShippingFees"
													? "Customer pays return"
													: "Not set"}
										</p>
									</div>
								)}
								<EditableField
									label="Return policy URL"
									type="url"
									value={
										editingDetails
											? editReturnPolicyUrl
											: (merchant.returnPolicyUrl ?? "")
									}
									editing={editingDetails}
									onChange={setEditReturnPolicyUrl}
									placeholder="https://shop.dk/returns"
								/>
							</div>
							<div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-100">
								<div>
									<Label>Created</Label>
									<p className="text-xs text-slate-500">
										{new Date(merchant.createdAt).toLocaleString("da-DK")}
									</p>
								</div>
								<div>
									<Label>Updated</Label>
									<p className="text-xs text-slate-500">
										{new Date(merchant.updatedAt).toLocaleString("da-DK")}
									</p>
								</div>
							</div>
						</div>
					</Section>
				</div>
			</div>

			{/* ────────── Contact Information ────────── */}
			<Section
				title="Contact Information"
				icon={Mail}
				actions={sectionEditActions(
					editingContact,
					savingSection === "contact",
					() => setEditingContact(true),
					handleSaveContact,
					handleCancelContact,
				)}
			>
				<div className="grid grid-cols-2 md:grid-cols-3 gap-4">
					<EditableField
						label="Contact Person"
						value={
							editingContact
								? editContactPerson
								: (merchant.contactPerson ?? "")
						}
						editing={editingContact}
						onChange={setEditContactPerson}
						placeholder="Full name"
					/>
					<EditableField
						label="Email"
						value={
							editingContact ? editContactEmail : (merchant.contactEmail ?? "")
						}
						editing={editingContact}
						onChange={setEditContactEmail}
						type="email"
						placeholder="email@example.com"
					/>
					<EditableField
						label="Phone"
						value={editingContact ? editPhone : (merchant.phone ?? "")}
						editing={editingContact}
						onChange={setEditPhone}
						placeholder="+45 12 34 56 78"
					/>
					<EditableField
						label="Address"
						value={editingContact ? editAddress : (merchant.address ?? "")}
						editing={editingContact}
						onChange={setEditAddress}
						placeholder="Street address"
					/>
					<div className="grid grid-cols-2 gap-2">
						<EditableField
							label="Zipcode"
							value={
								editingContact
									? editZipcode
									: (merchant.zipcode?.toString() ?? "")
							}
							editing={editingContact}
							onChange={setEditZipcode}
							placeholder="1000"
						/>
						<EditableField
							label="City"
							value={editingContact ? editCity : (merchant.city ?? "")}
							editing={editingContact}
							onChange={setEditCity}
							placeholder="Copenhagen"
						/>
					</div>
					<EditableField
						label="Country"
						value={editingContact ? editCountry : (merchant.country ?? "")}
						editing={editingContact}
						onChange={setEditCountry}
						placeholder="Denmark"
					/>
					<EditableField
						label="Trustpilot Score"
						value={
							editingContact
								? editTrustPilot
								: (merchant.trustPilot?.toString() ?? "")
						}
						editing={editingContact}
						onChange={setEditTrustPilot}
						type="number"
						placeholder="Score 1-5"
					/>
					<EditableField
						label="Trustpilot URL"
						value={
							editingContact
								? editTrustPilotUrl
								: (merchant.trustPilotUrl ?? "")
						}
						editing={editingContact}
						onChange={setEditTrustPilotUrl}
						placeholder="https://trustpilot.com/..."
					/>
				</div>
				{/* Display-only summary when not editing */}
				{!editingContact && (
					<div className="flex items-center gap-6 mt-4 pt-4 border-t border-slate-100 text-xs text-slate-400">
						{merchant.contactEmail && (
							<span className="flex items-center gap-1">
								<Mail className="w-3 h-3" /> {merchant.contactEmail}
							</span>
						)}
						{merchant.phone && (
							<span className="flex items-center gap-1">
								<Phone className="w-3 h-3" /> {merchant.phone}
							</span>
						)}
						{(merchant.city || merchant.country) && (
							<span className="flex items-center gap-1">
								<MapPin className="w-3 h-3" />{" "}
								{[merchant.city, merchant.country].filter(Boolean).join(", ")}
							</span>
						)}
						{merchant.trustPilot != null && (
							<span className="flex items-center gap-1">
								<Star className="w-3 h-3" /> Trustpilot: {merchant.trustPilot}
							</span>
						)}
					</div>
				)}
			</Section>

			{/* ────────── Marketing & Ads ────────── */}
			<Section
				title="Marketing & Ads"
				icon={Tag}
				actions={sectionEditActions(
					editingMarketing,
					savingSection === "marketing",
					() => setEditingMarketing(true),
					handleSaveMarketing,
					handleCancelMarketing,
				)}
			>
				<div className="space-y-4">
					<EditableField
						label="Affiliate ID"
						value={
							editingMarketing ? editAffiliateId : (merchant.affiliateId ?? "")
						}
						editing={editingMarketing}
						onChange={setEditAffiliateId}
						placeholder="Affiliate identifier"
					/>
					<div className="grid grid-cols-3 gap-4">
						<EditableToggle
							label="SEM / PPC"
							value={editingMarketing ? editSemPpc : merchant.semPpc}
							editing={editingMarketing}
							onChange={setEditSemPpc}
						/>
						<EditableToggle
							label="Shopping Ads"
							value={editingMarketing ? editShoppingAds : merchant.shoppingAds}
							editing={editingMarketing}
							onChange={setEditShoppingAds}
						/>
						<EditableToggle
							label="Social PPC"
							value={editingMarketing ? editSocialPpc : merchant.socialPpc}
							editing={editingMarketing}
							onChange={setEditSocialPpc}
						/>
					</div>
				</div>
			</Section>

			{/* ────────── Partner-ads Program (read-only, feed-sourced) ────────── */}
			<Section title="Partner-ads Program" icon={Percent}>
				<div className="space-y-4">
					<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
						<div>
							<Label>Commission</Label>
							<p className="text-sm text-slate-700">
								{merchant.commissionRate ? `${merchant.commissionRate}%` : "—"}
							</p>
						</div>
						<div>
							<Label>Click rate</Label>
							<p className="text-sm text-slate-700">
								{merchant.clickRate ? `${merchant.clickRate} kr` : "—"}
							</p>
						</div>
						<div>
							<Label>Lead rate</Label>
							<p className="text-sm text-slate-700">
								{merchant.leadRate ? `${merchant.leadRate} kr` : "—"}
							</p>
						</div>
						<div>
							<Label>EPC</Label>
							<p className="text-sm text-slate-700">
								{merchant.epc ? `${merchant.epc} kr` : "—"}
							</p>
						</div>
					</div>
					<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
						<div>
							<Label>Cashback allowed</Label>
							<p className="text-sm text-slate-700">
								{merchant.cashback === null
									? "—"
									: merchant.cashback
										? "Yes"
										: "No"}
							</p>
						</div>
						<div>
							<Label>Discount sites</Label>
							<p className="text-sm text-slate-700">
								{merchant.discountSites === null
									? "—"
									: merchant.discountSites
										? "Yes"
										: "No"}
							</p>
						</div>
						<div>
							<Label>Feed currency</Label>
							<p className="text-sm text-slate-700">
								{merchant.feedCurrency ?? "—"}
							</p>
						</div>
						<div>
							<Label>Feed market</Label>
							<p className="text-sm text-slate-700">
								{merchant.feedMarket ?? "—"}
							</p>
						</div>
					</div>
					{merchant.affiliateLink && (
						<div>
							<Label>Affiliate link</Label>
							<a
								href={merchant.affiliateLink}
								target="_blank"
								rel="noreferrer"
								className="text-sm text-primary inline-flex items-center gap-1 break-all hover:underline"
							>
								{merchant.affiliateLink}
								<ExternalLink className="w-3 h-3 shrink-0" />
							</a>
						</div>
					)}
					{(merchant.semPpcRestriction || merchant.shoppingAdsRestriction) && (
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							{merchant.semPpcRestriction && (
								<div>
									<Label>SEM/PPC restriction</Label>
									<p className="text-sm text-slate-700 whitespace-pre-wrap">
										{merchant.semPpcRestriction}
									</p>
								</div>
							)}
							{merchant.shoppingAdsRestriction && (
								<div>
									<Label>Shopping Ads restriction</Label>
									<p className="text-sm text-slate-700 whitespace-pre-wrap">
										{merchant.shoppingAdsRestriction}
									</p>
								</div>
							)}
						</div>
					)}
					{merchant.programTerms && (
						<div>
							<Label>Program terms</Label>
							<p className="text-sm text-slate-600 whitespace-pre-wrap max-h-48 overflow-y-auto">
								{merchant.programTerms}
							</p>
						</div>
					)}
				</div>
			</Section>

			{/* ────────── Trust & Policies (scraped) ────────── */}
			<Section
				title="Trust & Policies"
				icon={BadgeCheck}
				badge={
					merchant.scrapedAt ? (
						<span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-500">
							<Clock className="w-3 h-3" />
							Scraped {new Date(merchant.scrapedAt).toLocaleDateString("da-DK")}
						</span>
					) : (
						<span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-600">
							Never scraped
						</span>
					)
				}
				actions={sectionEditActions(
					editingTrust,
					savingSection === "trust",
					() => setEditingTrust(true),
					handleSaveTrust,
					handleCancelTrust,
				)}
			>
				<div className="space-y-5">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						<div>
							<Label>
								<CreditCard className="inline w-3 h-3 mr-1" />
								Payment methods
							</Label>
							{merchant.paymentMethods?.length ? (
								<div className="flex flex-wrap gap-1.5">
									{merchant.paymentMethods.map((p) => (
										<span
											key={p}
											className="inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 text-slate-600"
										>
											{p}
										</span>
									))}
								</div>
							) : (
								<p className="text-sm text-slate-400 italic">None detected</p>
							)}
						</div>
						<div>
							<Label>
								<ShieldCheck className="inline w-3 h-3 mr-1" />
								Trust marks
							</Label>
							{merchant.trustMarks?.length ? (
								<div className="flex flex-wrap gap-1.5">
									{merchant.trustMarks.map((t) => (
										<span
											key={t}
											className="inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium bg-emerald-50 text-emerald-600"
										>
											{t}
										</span>
									))}
								</div>
							) : (
								<p className="text-sm text-slate-400 italic">None detected</p>
							)}
						</div>
					</div>

					<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
						<EditableField
							label="Trustpilot rating"
							type="number"
							value={
								editingTrust
									? editTrustpilotRating
									: (merchant.trustpilotRating ?? "")
							}
							editing={editingTrust}
							onChange={setEditTrustpilotRating}
							placeholder="0.0–5.0"
						/>
						<EditableField
							label="Trustpilot reviews"
							type="number"
							value={
								editingTrust
									? editTrustpilotReviewCount
									: (merchant.trustpilotReviewCount?.toString() ?? "")
							}
							editing={editingTrust}
							onChange={setEditTrustpilotReviewCount}
						/>
						<EditableField
							label="Delivery days (min)"
							type="number"
							value={
								editingTrust
									? editDeliveryTimeMinDays
									: (merchant.deliveryTimeMinDays?.toString() ?? "")
							}
							editing={editingTrust}
							onChange={setEditDeliveryTimeMinDays}
						/>
						<EditableField
							label="Delivery days (max)"
							type="number"
							value={
								editingTrust
									? editDeliveryTimeMaxDays
									: (merchant.deliveryTimeMaxDays?.toString() ?? "")
							}
							editing={editingTrust}
							onChange={setEditDeliveryTimeMaxDays}
						/>
					</div>

					<div className="grid grid-cols-2 gap-4">
						<EditableField
							label="Free shipping over (kr)"
							type="number"
							value={
								editingTrust
									? editFreeShippingThreshold
									: (merchant.freeShippingThreshold ?? "")
							}
							editing={editingTrust}
							onChange={setEditFreeShippingThreshold}
						/>
						<EditableField
							label="Shipping from (kr)"
							type="number"
							value={
								editingTrust
									? editShippingCostFrom
									: (merchant.shippingCostFrom ?? "")
							}
							editing={editingTrust}
							onChange={setEditShippingCostFrom}
						/>
					</div>

					<EditableField
						label="Warranty / guarantees"
						value={
							editingTrust ? editWarrantyInfo : (merchant.warrantyInfo ?? "")
						}
						editing={editingTrust}
						onChange={setEditWarrantyInfo}
						multiline
						placeholder="e.g. 2 års reklamationsret"
					/>
					<EditableField
						label="Refund terms"
						value={editingTrust ? editRefundInfo : (merchant.refundInfo ?? "")}
						editing={editingTrust}
						onChange={setEditRefundInfo}
						multiline
					/>
					{merchant.scrapeSourceUrl && (
						<p className="text-[11px] text-slate-400 flex items-center gap-1">
							<Truck className="w-3 h-3" />
							Source: {merchant.scrapeSourceUrl}
						</p>
					)}
				</div>
			</Section>

			{id && <ShopTextPanel merchantId={id} />}

			{merchant.tier === "AFFILIATE" && id && (
				<CssInvitePanel
					merchantId={id}
					affiliatePcss={!!merchant.affiliatePcss}
				/>
			)}

			{/* ────────── Acquisition evidence (Partner-ads) ────────── */}
			<Section
				title="Acquisition Evidence"
				icon={TrendingUp}
				actions={
					<select
						className="px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-600 outline-none focus:border-primary/50"
						value={acquisitionWindowDays}
						onChange={(e) => setAcquisitionWindowDays(Number(e.target.value))}
					>
						{ACQUISITION_WINDOWS.map((w) => (
							<option key={w.days} value={w.days}>
								{w.label}
							</option>
						))}
					</select>
				}
			>
				{acquisitionLoading ? (
					<div className="flex items-center justify-center py-8">
						<div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
					</div>
				) : acquisitionError || !acquisition ? (
					<p className="text-sm text-slate-400">
						Kunne ikke hente acquisition-data.
					</p>
				) : !acquisition.programId ? (
					<p className="text-sm text-slate-500">
						Ingen Partner-ads program-id på denne merchant — der er intet
						Partner-ads-datagrundlag at vise. (Den tilsvarende visning for
						direkte/non-affiliate merchants via outbound_clicks er endnu ikke
						bygget — afhænger af L4.)
					</p>
				) : (
					<div className="space-y-4">
						<p className="text-xs text-slate-400">
							Kilde: {acquisition.source},{" "}
							{new Date(acquisition.window.from).toLocaleDateString("da-DK")} –{" "}
							{new Date(acquisition.window.to).toLocaleDateString("da-DK")} (
							{acquisition.days} dage)
						</p>
						<div className="grid grid-cols-2 md:grid-cols-3 gap-4">
							<div>
								<Label>Clicks leveret</Label>
								<p className="text-lg font-semibold text-slate-700">
									{acquisition.clicksCoverage === "none"
										? "—"
										: acquisition.clicks?.toLocaleString("da-DK")}
								</p>
								{acquisition.clicksCoverage !== "full" && (
									<p className="text-[11px] text-slate-400">
										{acquisition.clicksCoverage === "none"
											? "Ikke tilgængeligt for denne periode"
											: `Kun kendt for ${acquisition.daysWithClicks} af ${acquisition.days} dage`}
									</p>
								)}
							</div>
							<div>
								<Label>Konverteringer</Label>
								<p className="text-lg font-semibold text-slate-700">
									{acquisition.conversions.toLocaleString("da-DK")}
								</p>
							</div>
							<div>
								<Label>Ordreværdi</Label>
								<p className="text-lg font-semibold text-slate-700">
									{kr(acquisition.revenueDkk)}
								</p>
							</div>
							<div>
								<Label>Betalt til os</Label>
								<p className="text-lg font-semibold text-slate-700">
									{kr(acquisition.paidDkk)}
								</p>
							</div>
							<div>
								<Label>Effektiv CPC i dag</Label>
								<p className="text-lg font-semibold text-slate-700">
									{acquisition.effectiveCpcDkk != null
										? kr(acquisition.effectiveCpcDkk)
										: "—"}
								</p>
								{acquisition.effectiveCpcDkk == null && (
									<p className="text-[11px] text-slate-400">
										Kræver kendte clicks for hele perioden
									</p>
								)}
							</div>
							<div>
								<Label>Commerce Hub CPC</Label>
								<p className="text-lg font-semibold text-primary">
									{kr(acquisition.commerceHubCpcDkk)}
								</p>
							</div>
						</div>
					</div>
				)}
			</Section>

			{/* ────────── Lifecycle emails (subscription merchants only) ────────── */}
			{merchant.tier !== "AFFILIATE" && (
				<Section title="Lifecycle emails" icon={Mail}>
					<p className="text-sm text-slate-500 mb-4">
						Send en af abonnements-e-mailene manuelt til{" "}
						<span className="font-medium text-slate-700">
							{merchant.contactEmail || "— ingen kontaktadresse"}
						</span>
						. Skabelonerne er de samme som webappen sender automatisk, og kan
						redigeres i Strapi Email Designer.
					</p>
					<div className="flex flex-wrap gap-2">
						{LIFECYCLE_EMAILS.map((tpl) => (
							<button
								key={tpl.slug}
								type="button"
								disabled={!merchant.contactEmail || sendingEmail !== null}
								onClick={() => handleSendLifecycleEmail(tpl.slug)}
								className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:border-primary/50 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all"
							>
								<Send className="w-3.5 h-3.5" />
								{sendingEmail === tpl.slug ? "Sender…" : tpl.label}
							</button>
						))}
					</div>
					{emailResult && (
						<div
							className={`mt-4 inline-flex items-center gap-2 text-sm ${
								emailResult.ok ? "text-success" : "text-red-600"
							}`}
						>
							{emailResult.ok ? (
								<Check className="w-4 h-4" />
							) : (
								<AlertCircle className="w-4 h-4" />
							)}
							{emailResult.message}
						</div>
					)}
				</Section>
			)}

			{/* ────────── Feeds ────────── */}
			<Section
				title="Feeds"
				icon={Rss}
				badge={
					<span className="text-xs font-normal text-slate-400 ml-1">
						({merchant.feeds.length})
					</span>
				}
			>
				{merchant.feeds.length > 0 ? (
					<div className="-m-6 overflow-x-auto">
						<table className="w-full text-sm">
							<thead>
								<tr className="bg-slate-50 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
									<th className="px-6 py-3">Name</th>
									<th className="px-6 py-3">Type</th>
									<th className="px-6 py-3">Schedule</th>
									<th className="px-6 py-3">Products</th>
									<th className="px-6 py-3">Last Run</th>
									<th className="px-6 py-3">Status</th>
									<th className="px-6 py-3" />
								</tr>
							</thead>
							<tbody className="divide-y divide-slate-100">
								{merchant.feeds.map((feed) => (
									<tr
										key={feed.id}
										className="hover:bg-slate-50/50 transition-colors"
									>
										<td className="px-6 py-3.5">
											<p className="font-medium text-slate-700">{feed.name}</p>
										</td>
										<td className="px-6 py-3.5">
											<span
												className={`inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${FEED_TYPE_COLORS[feed.feedType] ?? "bg-slate-100 text-slate-500"}`}
											>
												{feed.feedType.replace(/_/g, " ")}
											</span>
										</td>
										<td className="px-6 py-3.5">
											<span className="flex items-center gap-1 text-xs text-slate-500">
												<Clock className="w-3 h-3" />
												{feed.schedule}
											</span>
										</td>
										<td className="px-6 py-3.5 text-slate-500">
											{feed.assumedProductCount.toLocaleString()}
										</td>
										<td className="px-6 py-3.5 text-xs text-slate-500">
											{feed.lastRun
												? new Date(feed.lastRun).toLocaleString("da-DK")
												: "Never"}
										</td>
										<td className="px-6 py-3.5">
											<span
												className={`inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${
													feed.active
														? "bg-success/10 text-success"
														: "bg-slate-100 text-slate-400"
												}`}
											>
												{feed.active ? "Active" : "Inactive"}
											</span>
										</td>
										<td className="px-6 py-3.5">
											<a
												href={feed.url}
												target="_blank"
												rel="noopener noreferrer"
												className="text-primary hover:text-primary-dark transition-colors"
												title="Open feed URL"
											>
												<ExternalLink className="w-4 h-4" />
											</a>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				) : (
					<div className="flex flex-col items-center justify-center py-8 text-slate-400">
						<Rss className="w-10 h-10 mb-2 text-slate-300" />
						<p className="text-sm">No feeds configured for this merchant</p>
					</div>
				)}
			</Section>

			{/* ────────── Quick Links ────────── */}
			<div className="flex items-center gap-3">
				{merchant.website && (
					<a
						href={
							merchant.website.startsWith("http")
								? merchant.website
								: `https://${merchant.website}`
						}
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-slate-600 bg-white border border-slate-200 hover:border-primary/40 hover:text-primary transition-colors"
					>
						<Globe className="w-4 h-4" /> Visit Website
					</a>
				)}
				{merchant.trustPilotUrl && (
					<a
						href={merchant.trustPilotUrl}
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-slate-600 bg-white border border-slate-200 hover:border-primary/40 hover:text-primary transition-colors"
					>
						<Star className="w-4 h-4" /> Trustpilot
					</a>
				)}
			</div>
		</div>
	);
};

export default Merchant;
